import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useEffect } from "react";
import type { Address } from "viem";
import { maxUint256 } from "viem";
import { LOCKED_LP_STAKING_ABI, PIGGY_TOKEN_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const ZERO: Address = "0x0000000000000000000000000000000000000000";

export interface LpStakeData {
    lpToken: Address;
    walletBalance: bigint;
    allowance: bigint;
    stakedAmount: bigint;
    lockEnd: bigint;
    multiplierBps: number;
    earned: bigint;
    totalStaked: bigint;
    totalWeightedStake: bigint;
    totalReceived: bigint;
    effectiveMultiplierBps: number;
}

const DEFAULTS: LpStakeData = {
    lpToken: ZERO,
    walletBalance: 0n,
    allowance: 0n,
    stakedAmount: 0n,
    lockEnd: 0n,
    multiplierBps: 10_000,
    earned: 0n,
    totalStaked: 0n,
    totalWeightedStake: 0n,
    totalReceived: 0n,
    effectiveMultiplierBps: 10_000,
};

export function useLpStakeData(): { data: LpStakeData; live: boolean; refetch: () => void } {
    const { address } = useAccount();
    const staking = ADDRESSES.LOCKED_LP_STAKING;
    const user: Address = address ?? ZERO;

    // First fetch the LP token address from the staking contract, then read
    // balance / allowance against that address. Two-stage multicall.
    const stakingReads = useReadContracts({
        allowFailure: true,
        contracts: staking
            ? [
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "lpToken" } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "totalStaked" } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "totalWeightedStake" } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "totalReceived" } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "earned", args: [user] } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "userStakeOf", args: [user] } as const,
                  { address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "previewEffectiveMultiplier", args: [user] } as const,
              ]
            : undefined,
        query: { enabled: Boolean(staking), refetchInterval: 15_000 },
    });

    const lpTokenAddr = stakingReads.data?.[0]?.result as Address | undefined;
    const validLp = lpTokenAddr && lpTokenAddr !== "0x000000000000000000000000000000000000DeAd"
        && lpTokenAddr !== "0x000000000000000000000000000000000000dEaD"
        && lpTokenAddr !== ZERO;

    const lpReads = useReadContracts({
        allowFailure: true,
        contracts: validLp && address && staking
            ? [
                  { address: lpTokenAddr, abi: PIGGY_TOKEN_ABI, functionName: "balanceOf", args: [user] } as const,
                  { address: lpTokenAddr, abi: PIGGY_TOKEN_ABI, functionName: "allowance", args: [user, staking] } as const,
              ]
            : undefined,
        query: { enabled: Boolean(validLp && address && staking), refetchInterval: 15_000 },
    });

    if (!staking || !stakingReads.data) {
        return { data: DEFAULTS, live: false, refetch: () => stakingReads.refetch() };
    }

    const userState = stakingReads.data[5]?.result as
        { amount: bigint; lockEnd: bigint; multiplierBps: number } | undefined;

    return {
        data: {
            lpToken:            lpTokenAddr ?? ZERO,
            walletBalance:      (lpReads.data?.[0]?.result as bigint | undefined) ?? 0n,
            allowance:          (lpReads.data?.[1]?.result as bigint | undefined) ?? 0n,
            stakedAmount:       userState?.amount ?? 0n,
            lockEnd:            userState?.lockEnd ?? 0n,
            multiplierBps:      Number(userState?.multiplierBps ?? 10_000),
            earned:             (stakingReads.data[4]?.result as bigint | undefined) ?? 0n,
            totalStaked:        (stakingReads.data[1]?.result as bigint | undefined) ?? 0n,
            totalWeightedStake: (stakingReads.data[2]?.result as bigint | undefined) ?? 0n,
            totalReceived:      (stakingReads.data[3]?.result as bigint | undefined) ?? 0n,
            effectiveMultiplierBps: Number((stakingReads.data[6]?.result as number | undefined) ?? 10_000),
        },
        live: true,
        refetch: () => {
            stakingReads.refetch();
            lpReads.refetch();
        },
    };
}

function useTxStatus() {
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });
    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);
    return {
        writeContract, hash,
        status: confirmed ? "confirmed" : confirming ? "confirming" : isPending ? "pending" : "idle",
    } as const;
}

export function useApproveLp(lpToken: Address) {
    const staking = ADDRESSES.LOCKED_LP_STAKING;
    const tx = useTxStatus();
    const approve = () => {
        if (!staking || !lpToken) return;
        tx.writeContract({
            address: lpToken,
            abi: PIGGY_TOKEN_ABI,
            functionName: "approve",
            args: [staking, maxUint256],
        });
    };
    return { approve, ...tx };
}

export function useStakeLp(amount: bigint, tier: number) {
    const staking = ADDRESSES.LOCKED_LP_STAKING;
    const tx = useTxStatus();
    const stake = () => {
        if (!staking || amount === 0n) return;
        tx.writeContract({
            address: staking,
            abi: LOCKED_LP_STAKING_ABI,
            functionName: "stake",
            args: [amount, tier],
        });
    };
    return { stake, ...tx };
}

export function useUnstakeLp(amount: bigint) {
    const staking = ADDRESSES.LOCKED_LP_STAKING;
    const tx = useTxStatus();
    const unstake = () => {
        if (!staking || amount === 0n) return;
        tx.writeContract({
            address: staking,
            abi: LOCKED_LP_STAKING_ABI,
            functionName: "unstake",
            args: [amount],
        });
    };
    return { unstake, ...tx };
}

export function useClaimLp() {
    const staking = ADDRESSES.LOCKED_LP_STAKING;
    const tx = useTxStatus();
    const claim = () => {
        if (!staking) return;
        tx.writeContract({ address: staking, abi: LOCKED_LP_STAKING_ABI, functionName: "claim", args: [] });
    };
    return { claim, ...tx };
}
